import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import { Menu as MenuIcon, Bell } from "lucide-react";

import logo from "../assets/images/logo-01-1.png";
import { cn } from "lib/utils";
import { getAvatarUrl } from "helpers/avatar";
import { Button } from "components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { DirectChatUser } from "slices/communication/types";

interface HeaderProps {
  onMobileMenuToggle?: () => void;
}

const Header = ({ onMobileMenuToggle }: HeaderProps) => {
  const navigate = useNavigate();
  const { contacts } = useSelector((state: any) => state.Communication);

  // Unread direct messages from the Communication module.
  const unreadContacts = useMemo(
    () =>
      (contacts as DirectChatUser[]).filter((c) => (c.unreadCount || 0) > 0),
    [contacts]
  );
  const hasUnread = unreadContacts.length > 0;
  const totalUnread = useMemo(
    () => unreadContacts.reduce((n, c) => n + (c.unreadCount || 0), 0),
    [unreadContacts]
  );

  // Open the exact conversation thread with the given sender.
  const openConversation = (userId: string) =>
    navigate("/communication", { state: { userId } });

  // Animate the bell + flash a short preview whenever a new message arrives.
  const prevUnreadRef = useRef(totalUnread);
  const [ringing, setRinging] = useState(false);
  const [preview, setPreview] = useState<DirectChatUser | null>(null);
  const ringTimer = useRef<ReturnType<typeof setTimeout>>();
  const previewTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (totalUnread > prevUnreadRef.current) {
      // The most recently updated unread contact is the new sender.
      const latest = [...unreadContacts].sort(
        (a, b) =>
          new Date(b.lastMessageDateTime || 0).getTime() -
          new Date(a.lastMessageDateTime || 0).getTime()
      )[0];

      setRinging(true);
      clearTimeout(ringTimer.current);
      ringTimer.current = setTimeout(() => setRinging(false), 900);

      if (latest) {
        setPreview(latest);
        clearTimeout(previewTimer.current);
        // Show the preview briefly, then fade it out.
        previewTimer.current = setTimeout(() => setPreview(null), 2000);
      }
    }
    prevUnreadRef.current = totalUnread;
  }, [totalUnread, unreadContacts]);

  useEffect(
    () => () => {
      clearTimeout(ringTimer.current);
      clearTimeout(previewTimer.current);
    },
    []
  );

  return (
    <motion.header
      initial={{ y: -16, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className={cn(
        "sticky top-0 z-40 flex h-16 items-center gap-3 px-4 sm:px-6",
        "border-b border-border bg-card shadow-sm"
      )}
    >
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={onMobileMenuToggle}
        aria-label="Open menu"
      >
        <MenuIcon className="h-5 w-5" />
      </Button>

      <Link
        to="/"
        className="flex items-center gap-2 font-semibold text-foreground"
      >
        <img src={logo} alt="EngageHub IQ" className="h-10 w-auto sm:h-12" />
        <span className="hidden bg-gradient-to-r from-primary to-accent bg-clip-text text-base font-semibold tracking-tight text-transparent sm:inline">
          EngageHub IQ
        </span>
      </Link>

      <div className="relative ml-auto flex items-center gap-2 sm:gap-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <motion.button
              aria-label="Notifications"
              animate={
                ringing
                  ? { rotate: [0, -15, 12, -10, 8, -4, 0] }
                  : { rotate: 0 }
              }
              transition={{ duration: 0.9, ease: "easeInOut" }}
              className={cn(
                "relative grid h-9 w-9 place-items-center rounded-full",
                "transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              )}
            >
              <Bell className="h-5 w-5 text-muted-foreground" />
              {hasUnread && (
                <motion.span
                  key={totalUnread}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 500, damping: 18 }}
                  className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-destructive ring-2 ring-card"
                />
              )}
              {/* Expanding pulse ring when a new message lands. */}
              {ringing && (
                <span className="pointer-events-none absolute inset-0 animate-ping rounded-full bg-primary/30" />
              )}
            </motion.button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <DropdownMenuLabel className="flex items-center justify-between">
              <span>Notifications</span>
              {hasUnread && (
                <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-[11px] font-medium text-destructive">
                  {totalUnread} new
                </span>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {!hasUnread ? (
              <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                You're all caught up.
              </div>
            ) : (
              unreadContacts.map((contact) => (
                <DropdownMenuItem
                  key={contact.userId}
                  className="flex items-start gap-3"
                  onClick={() => openConversation(contact.userId)}
                >
                  <img
                    src={getAvatarUrl(contact.email || contact.userId)}
                    alt={contact.name}
                    className="h-9 w-9 shrink-0 rounded-full bg-muted"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium text-foreground">
                        {contact.name}
                      </span>
                      <span className="grid h-5 min-w-5 shrink-0 place-items-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground">
                        {contact.unreadCount}
                      </span>
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                      {contact.lastMessageContent || "New message"}
                    </span>
                  </span>
                </DropdownMenuItem>
              ))
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Transient preview of the incoming message. */}
        <AnimatePresence>
          {preview && (
            <motion.button
              type="button"
              initial={{ opacity: 0, y: -8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.96 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              onClick={() => {
                setPreview(null);
                openConversation(preview.userId);
              }}
              className={cn(
                "absolute right-0 top-12 z-50 flex w-72 items-start gap-3 rounded-xl p-3 text-left",
                "border border-border bg-popover shadow-soft-lg"
              )}
            >
              <img
                src={getAvatarUrl(preview.email || preview.userId)}
                alt={preview.name}
                className="h-9 w-9 shrink-0 rounded-full bg-muted"
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-foreground">
                  {preview.name}
                </span>
                <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                  {preview.lastMessageContent || "New message"}
                </span>
              </span>
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Header;
